import { useEffect, useState } from "react";
import { Quote, Heart, Send, Loader2, ImagePlus, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { FadeUp } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { TESTIMONIAL_META, formatDate } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_IDS = ["all", "adhd", "projects", "wedding"];
const STORY_CATEGORIES = ["adhd", "projects", "wedding", "other"];

const StoryForm = () => {
  const { t, lang } = useLang();
  const tp = t.testimonialsPage;
  const [form, setForm] = useState({ name: "", category: "adhd", email: "", message: "", customCategory: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const photoUrl = photo ? URL.createObjectURL(photo) : null;

  const handlePhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error(tp.photoError);
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(tp.photoError);
      e.target.value = "";
      return;
    }
    setPhoto(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("category", form.category);
      data.append("email", form.email);
      data.append("message", form.message);
      if (form.category === "other" && form.customCategory.trim()) {
        data.append("custom_category", form.customCategory.trim());
      }
      data.append("lang", lang);
      if (photo) data.append("photo", photo);
      const res = await fetch(`${API}/testimonials`, {
        method: "POST",
        body: data,
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
    } catch (err) {
      toast.error(tp.errorToast);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-border bg-linen px-4 py-3 text-sm text-espresso placeholder:text-taupe/70 transition-colors focus:border-terracotta focus:outline-none";

  return (
    <section data-testid="story-form-section" className="max-w-2xl mx-auto px-4 sm:px-8 pb-20">
      <FadeUp>
        <div className="rounded-3xl bg-white border border-border p-8 sm:p-10 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)]">
          {submitted ? (
            <div className="text-center py-6" data-testid="story-success">
              <span className="w-16 h-16 rounded-full bg-terracotta-soft flex items-center justify-center mx-auto mb-5">
                <Heart className="w-7 h-7 text-terracotta" />
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{tp.successTitle}</h2>
              <p className="mt-3 text-sm sm:text-base text-clay leading-relaxed max-w-md mx-auto">{tp.successText}</p>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta">{tp.formKicker}</p>
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold mt-2">{tp.formTitle}</h2>
              <p className="mt-3 text-sm text-clay leading-relaxed">{tp.formIntro}</p>
              <form onSubmit={handleSubmit} className="mt-7 space-y-5" data-testid="story-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="story-name" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                      {tp.nameLabel}
                    </label>
                    <input
                      id="story-name"
                      data-testid="story-name-input"
                      required
                      maxLength={80}
                      value={form.name}
                      onChange={set("name")}
                      placeholder={tp.namePlaceholder}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="story-category" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                      {tp.categoryLabel}
                    </label>
                    <select
                      id="story-category"
                      data-testid="story-category-select"
                      value={form.category}
                      onChange={set("category")}
                      className={inputCls}
                    >
                      {STORY_CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c === "other" ? tp.otherLabel : tp.filters[c]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {form.category === "other" && (
                  <div>
                    <label htmlFor="story-custom-category" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                      {tp.customCategoryLabel}
                    </label>
                    <input
                      id="story-custom-category"
                      data-testid="story-custom-category-input"
                      maxLength={40}
                      value={form.customCategory}
                      onChange={set("customCategory")}
                      placeholder={tp.customCategoryPlaceholder}
                      className={inputCls}
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="story-email" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                    {tp.emailLabel}
                  </label>
                  <input
                    id="story-email"
                    data-testid="story-email-input"
                    type="email"
                    maxLength={120}
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="story-message" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                    {tp.messageLabel}
                  </label>
                  <textarea
                    id="story-message"
                    data-testid="story-message-input"
                    required
                    minLength={10}
                    maxLength={2000}
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    placeholder={tp.messagePlaceholder}
                    className={`${inputCls} resize-none`}
                  />
                </div>
                <div>
                  <label htmlFor="story-photo" className="block text-xs font-semibold uppercase tracking-[0.15em] text-taupe mb-2">
                    {tp.photoLabel}
                  </label>
                  {photo ? (
                    <div className="flex items-center gap-4" data-testid="story-photo-preview">
                      <img src={photoUrl} alt={tp.photoLabel} className="w-16 h-16 rounded-full object-cover bg-cream border border-border" />
                      <div className="min-w-0">
                        <p className="text-sm text-espresso font-medium truncate max-w-[220px]">{photo.name}</p>
                        <button
                          type="button"
                          data-testid="story-photo-remove-btn"
                          onClick={() => setPhoto(null)}
                          className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-terracotta hover:text-terracotta-dark"
                        >
                          <X className="w-3.5 h-3.5" /> {tp.photoRemove}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <label
                        htmlFor="story-photo"
                        data-testid="story-photo-drop"
                        className="flex items-center gap-3 rounded-xl border border-dashed border-border bg-linen px-4 py-3.5 cursor-pointer transition-colors hover:border-terracotta hover:bg-cream/60"
                      >
                        <ImagePlus className="w-5 h-5 text-taupe shrink-0" />
                        <span className="text-sm text-taupe">{tp.photoHint}</span>
                      </label>
                      <input
                        id="story-photo"
                        data-testid="story-photo-input"
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handlePhoto}
                        className="sr-only"
                      />
                    </>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="story-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-terracotta text-linen px-8 py-4 text-base font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? tp.sending : tp.submit}
                </button>
              </form>
            </>
          )}
        </div>
      </FadeUp>
    </section>
  );
};

export default function Testimonials() {
  const { t, lang } = useLang();
  const tp = t.testimonialsPage;
  const [filter, setFilter] = useState("all");
  const [published, setPublished] = useState([]);

  useEffect(() => {
    fetch(`${API}/testimonials/published`)
      .then((r) => (r.ok ? r.json() : { stories: [] }))
      .then((d) => setPublished(d.stories || []))
      .catch(() => {});
  }, []);

  const staticStories = TESTIMONIAL_META.map((m) => ({
    key: m.id,
    name: m.name,
    cat: m.cat,
    date: m.date,
    avatar: m.avatar,
    customCat: null,
    quote: t.testimonialsData[m.id].quote,
  }));
  const liveStories = published.map((s) => ({
    key: `sub-${s.id}`,
    name: s.name,
    cat: s.category,
    date: (s.created_at || "").slice(0, 10),
    avatar: s.photo_path ? `${API}/files/${s.photo_path}` : null,
    customCat: (s.custom_category || "").trim() || null,
    quote: (s.translations && s.translations[lang]) || s.message,
  }));
  const all = [...liveStories, ...staticStories].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const customFilters = [...new Set(all.map((x) => x.customCat).filter(Boolean))];
  const visible =
    filter === "all"
      ? all
      : customFilters.includes(filter)
        ? all.filter((x) => x.customCat === filter)
        : all.filter((x) => x.cat === filter);

  const catLabel = (s) => s.customCat || (s.cat === "other" ? tp.otherLabel : tp.filters[s.cat] || s.cat);

  return (
    <>
      <section data-testid="testimonials-header" className="pt-32 sm:pt-40 pb-12 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{tp.kicker}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-balance">
            {tp.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-clay leading-relaxed">{tp.intro}</p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-10 flex flex-wrap gap-3" data-testid="testimonials-filters">
          {[...FILTER_IDS, ...customFilters].map((id) => {
            const isCustom = customFilters.includes(id);
            return (
              <button
                key={id}
                data-testid={isCustom ? `filter-custom-${id.toLowerCase().replace(/\s+/g, "-")}` : `filter-${id}`}
                onClick={() => setFilter(id)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-[background-color,border-color,color] duration-200 ${
                  filter === id
                    ? "bg-terracotta text-linen border-terracotta"
                    : "bg-white text-clay border-border hover:bg-cream"
                }`}
              >
                {isCustom ? id : tp.filters[id]}
              </button>
            );
          })}
        </FadeUp>
      </section>

      <section data-testid="testimonials-wall" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((s) => (
              <motion.figure
                layout
                key={s.key}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`testimonial-card-${s.key}`}
                className="flex flex-col rounded-3xl bg-white border border-border p-8 shadow-[0_16px_40px_-24px_rgba(44,37,32,0.2)]"
              >
                <Quote className="w-8 h-8 text-terracotta/50 mb-5" />
                <blockquote className="flex-1 font-serif text-xl leading-relaxed text-espresso">
                  &ldquo;{s.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-7 pt-6 border-t border-border flex items-center gap-4">
                  {s.avatar ? (
                    <img src={s.avatar} alt={s.name} className="w-14 h-14 rounded-full object-cover bg-cream" loading="lazy" />
                  ) : (
                    <span className="w-14 h-14 rounded-full bg-terracotta-soft text-terracotta flex items-center justify-center text-lg font-bold shrink-0">
                      {s.name.trim()[0]?.toUpperCase()}
                    </span>
                  )}
                  <div>
                    <p className="text-sm font-semibold">{s.name}</p>
                    <p className="text-xs text-taupe">
                      {catLabel(s)}
                      {s.date ? ` · ${formatDate(s.date, lang)}` : ""}
                    </p>
                  </div>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <StoryForm />

      <CtaBanner testid="testimonials-cta-banner" title={tp.cta.title} subtitle={tp.cta.subtitle} />
    </>
  );
}
